/**
 * Device-aware capture & codec selection (008-mobile-video-quality).
 *
 * Pure, framework-free helpers so they're unit-testable without a browser:
 *   - isMobileDevice(): coarse mobile-vs-desktop classification.
 *   - chooseVideoCodec(): which video codec to publish with, by device class.
 *
 * Why: iOS Safari has hardware H.264 but no usable VP9/VP8 hardware encoder.
 * Forcing it onto software VP8 (the pre-008 behaviour) made its outgoing video
 * glitch. Mobile therefore prefers H.264; desktop keeps VP9 (with SVC, 006).
 */

/** Minimal navigator shape we read — injectable for tests. */
export interface NavigatorLike {
  userAgent: string
  maxTouchPoints?: number
  userAgentData?: { mobile?: boolean }
}

/**
 * Coarse mobile detection (research.md D4). True when:
 *   - the UA-Client-Hints `mobile` flag is set (Chromium), or
 *   - the UA matches a phone/Android pattern, or
 *   - it's iPadOS masquerading as macOS (Macintosh UA + real touch points).
 * Tablets count as mobile (they share the hardware-encoding constraints).
 */
export function isMobileDevice(
  nav: NavigatorLike | undefined = typeof navigator !== 'undefined'
    ? (navigator as NavigatorLike)
    : undefined,
): boolean {
  if (!nav) return false
  if (nav.userAgentData?.mobile === true) return true

  const ua = nav.userAgent ?? ''
  if (/Android|iPhone|iPod/i.test(ua)) return true
  if (/iPad/i.test(ua)) return true
  // iPadOS 13+ reports a desktop "Macintosh" UA but exposes touch points.
  if (/Macintosh/i.test(ua) && (nav.maxTouchPoints ?? 0) > 1) return true

  return false
}

export interface VideoSendChoice {
  /**
   * Codec to FORCE via `produce({ codec })` — only ever H.264. We never force a
   * codec the device might not be able to ENCODE: forcing VP9 on a phone with no
   * VP9 hardware encoder (iOS) drops it to a brutal software VP9 encode. H.264 is
   * always hardware-encodable on the mobiles we target, so it's safe to force.
   */
  forceMimeType?: 'video/H264'
  /** SVC scalabilityMode for the encoding (desktop VP9 only); undefined elsewhere. */
  scalabilityMode?: string
}

/**
 * Decide how to publish video, by device class (research.md D2).
 *
 * The golden rule: **only ever force H.264** (always hardware-encodable on
 * phones), and otherwise force nothing — mediasoup-client then selects the first
 * codec it can actually ENCODE in router order (VP9 → H264 → VP8), which is
 * correct for every device:
 *   - Desktop (can encode VP9) → VP9, and we enable K-SVC (006).
 *   - iOS (no VP9/VP8 hardware encoder) → H.264 (hardware), automatically.
 *
 * - Mobile: force H.264 when offered (hardware path); never force VP9. No SVC.
 * - Desktop: force nothing; attach the SVC mode when VP9 is available (the mode is
 *   ignored if a non-VP9 codec ends up selected, so it's harmless).
 */
export function chooseVideoSend(
  available: ReadonlyArray<{ mimeType: string }>,
  opts: { mobile: boolean; scalabilityMode: string },
): VideoSendChoice {
  const has = (mime: string) =>
    available.some((c) => c.mimeType.toLowerCase() === mime.toLowerCase())

  if (opts.mobile) {
    return has('video/H264') ? { forceMimeType: 'video/H264' } : {}
  }
  return has('video/VP9') ? { scalabilityMode: opts.scalabilityMode } : {}
}
